import { useCallback, useState } from 'react'
import { Award, BadgeCheck, Briefcase, GraduationCap, MoreHorizontal, Trash2 } from 'lucide-react'

import {
  SkillManagerAddRow,
  SkillManagerEditableNameButton,
  SkillManagerEditComposer,
  SkillManagerTrailingCluster,
  skillManagerSkillRowClassName,
} from '@/components/skills/skill-manager-parts'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { SkillSummary } from '@/hooks/use-profile'
import { cn } from '@/lib/utils'
import type { UnifiedSkillItem } from '@/utils/profile-skills-display'

import { ProficiencyLabelBadge } from './proficiency-label-badge'

function SkillMetrics({ item }: { item: UnifiedSkillItem }) {
  if (item.type !== 'demonstrated') {
    return <span className="text-xs text-muted-foreground">No linked experience</span>
  }

  const proofTotal =
    item.summary.evidence_counts.experiences +
    item.summary.evidence_counts.education +
    item.summary.evidence_counts.certifications

  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <ProficiencyLabelBadge label={item.summary.proficiency_label} />
      <span className="inline-flex items-center gap-1 tabular-nums text-success">
        <BadgeCheck className="size-3.5 shrink-0" aria-hidden strokeWidth={2.5} />
        {proofTotal}
      </span>
    </span>
  )
}

export type SkillEvidenceLinkType = 'experience' | 'education' | 'certification'

function SkillRowActionsMenu({
  skillKey,
  disabled,
  onLinkExperience,
  onLinkEducation,
  onLinkCertification,
  onRemove,
}: {
  skillKey: string
  disabled?: boolean
  onLinkExperience: () => void
  onLinkEducation: () => void
  onLinkCertification: () => void
  onRemove: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)

  /** Close the menu before opening a nested dialog so Radix does not leave a blocking layer. */
  const runAfterMenuClosed = useCallback((action: () => void) => {
    setMenuOpen(false)
    window.requestAnimationFrame(() => {
      action()
    })
  }, [])

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          disabled={disabled}
          aria-label={`Actions for ${skillKey}`}
          title="Skill actions"
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuItem
          className="gap-2"
          onSelect={() => runAfterMenuClosed(onLinkExperience)}
        >
          <Briefcase className="size-4" aria-hidden />
          Link to experience
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2"
          onSelect={() => runAfterMenuClosed(onLinkEducation)}
        >
          <GraduationCap className="size-4" aria-hidden />
          Link to education
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2"
          onSelect={() => runAfterMenuClosed(onLinkCertification)}
        >
          <Award className="size-4" aria-hidden />
          Link to certification
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="gap-2 text-destructive focus:text-destructive"
          onSelect={() => runAfterMenuClosed(onRemove)}
        >
          <Trash2 className="size-4" aria-hidden />
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SkillManagerSkillRow({
  item,
  skillKey,
  isEditing,
  editDraft,
  editError,
  editHint,
  editExcludeNames,
  disabled,
  animateEntrance = false,
  onStartEdit,
  onEditDraftChange,
  onSaveEdit,
  onCancelEdit,
  onLinkExperience,
  onLinkEducation,
  onLinkCertification,
  onRemove,
}: {
  item: UnifiedSkillItem
  skillKey: string
  isEditing: boolean
  editDraft: string
  editError: string | null
  editHint?: string | null
  editExcludeNames: string[]
  disabled?: boolean
  animateEntrance?: boolean
  onStartEdit: () => void
  onEditDraftChange: (next: string) => void
  onSaveEdit: (name: string) => void
  onCancelEdit: () => void
  onLinkExperience: () => void
  onLinkEducation: () => void
  onLinkCertification: () => void
  onRemove: () => void
}) {
  const rowEnterClass = cn(
    animateEntrance &&
      'animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-in motion-reduce:animate-none',
  )

  if (isEditing) {
    return (
      <li className={cn(skillManagerSkillRowClassName, rowEnterClass)}>
        <SkillManagerEditComposer
          value={editDraft}
          onValueChange={onEditDraftChange}
          excludeNames={editExcludeNames}
          disabled={disabled}
          editError={editError}
          editHint={editHint}
          onCommit={onSaveEdit}
          onCancel={onCancelEdit}
        />
      </li>
    )
  }

  return (
    <li className={cn(skillManagerSkillRowClassName, rowEnterClass)}>
      <div className="flex items-center justify-between gap-3">
        <SkillManagerEditableNameButton name={skillKey} disabled={disabled} onStartEdit={onStartEdit} />
        <SkillManagerTrailingCluster
          leading={<SkillMetrics item={item} />}
          actions={
            <SkillRowActionsMenu
              skillKey={skillKey}
              disabled={disabled}
              onLinkExperience={onLinkExperience}
              onLinkEducation={onLinkEducation}
              onLinkCertification={onLinkCertification}
              onRemove={onRemove}
            />
          }
        />
      </div>
    </li>
  )
}

export { SkillManagerAddRow }

export type { SkillSummary }
